import { useState } from 'react';
import { Box, CircularProgress, IconButton, SvgIcon, Tooltip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReportDownloadDetail } from '../../Reporting/interfaces/ReportDownloadDetail';
import { Pill } from '../../../components/shared/Pill';
import {
  ReportStatus,
  ReportStatusTranslationMap,
  getReportStatusPalette,
} from '../../../enums/ReportStatus';
import { DownloadCloud01, RefreshCw05 } from '../../../assets';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';


interface RecentReportsPanelProps {
  reports: ReportDownloadDetail[];
  loading?: boolean;
  onRefresh?: () => void;
  refreshing?: boolean;
}

/**
 * Bottom-right panel showing the most recent reports the user has generated, each with a live
 * status pill (queued / processing / completed / failed) and a download action once Completed.
 * Gives immediate feedback after Generate without needing to switch to the history tab.
 * Rendered on-demand so status is never stale. The refresh control lives in this panel's header
 * since that is what it refreshes.
 */
const RecentReportsPanel = ({ reports, loading = false, onRefresh, refreshing = false }: RecentReportsPanelProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { getReport } = useNswagClient();
  const { logError } = useLogError();
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const recent = reports.slice(0, 5);

  const downloadReport = (report: ReportDownloadDetail) => {
    if (!report.id) {
      return;
    }
    setDownloadingId(report.id);
    getReport(report.id)
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${report.name ?? 'report'}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((error) => {
        logError(error);
      })
      .finally(() => {
        setDownloadingId(null);
      });
  };

  return (
    <Box
      sx={{
        // Match the Section card styling used by the Scope / Report details tiles.
        border: `1px solid ${theme.palette.custom.gray[300]}`,
        boxShadow: '0 1px 2px 1px #1018280D',
        borderRadius: '12px',
        overflow: 'hidden',
        minWidth: '300px',
        alignSelf: 'flex-start',
      }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: '20px',
        py: '14px',
        borderBottom: `1px solid ${theme.palette.custom.gray[200]}`,
      }}>
        <Typography variant="textLG"
          sx={{ fontWeight: 600 }}>
          {t('reportingV2.recent-reports')}
        </Typography>
        {onRefresh && (
          <Tooltip title={t('reporting.refresh')}>
            <span>
              <IconButton
                size="small"
                onClick={onRefresh}
                disabled={refreshing}
                aria-label={t('reporting.refresh')}>
                <SvgIcon
                  sx={refreshing
                    ? { width: '18px', animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }
                    : { width: '18px' }}>
                  <RefreshCw05 />
                </SvgIcon>
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      <Box sx={{ p: '20px' }}>
        {loading && recent.length === 0 ? (
          <Typography variant="textSM"
            sx={{ color: theme.palette.custom.gray[500], py: '8px' }}>
            {t('reportingV2.loading')}
          </Typography>
        ) : recent.length === 0 ? (
          <Typography variant="textSM"
            sx={{ color: theme.palette.custom.gray[500], py: '8px' }}>
            {t('reportingV2.no-recent-reports')}
          </Typography>
        ) : (
          recent.map((report) => (
            <Box key={report.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                py: '10px',
                borderTop: `solid 1px ${theme.palette.custom.gray[100]}`,
              }}>
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography variant="textSM"
                  noWrap>
                  {/* displayName is an i18n key only once setFriendlyName matched a known report prefix;
                      in-flight placeholders carry the raw report name, which isn't a key — render it
                      directly instead of calling t() (which would spam missingKey warnings). */}
                  {report.displayName?.includes('.') ? t(report.displayName) : (report.displayName ?? report.name ?? '')}
                </Typography>
                {report.createdAt && (
                  <Typography variant="textXS"
                    sx={{ color: theme.palette.custom.gray[500] }}>
                    {report.createdAt}
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                {report.status !== undefined && (
                  <Pill
                    text={t(ReportStatusTranslationMap[report.status as ReportStatus])}
                    pallet={getReportStatusPalette(report.status, theme)}
                  />
                )}
                {report.status === ReportStatus.Completed && (
                  <Tooltip title={t('reporting.download')}>
                    <span>
                      <IconButton
                        size="small"
                        aria-label={t('reporting.download')}
                        disabled={downloadingId === report.id}
                        onClick={() => downloadReport(report)}
                        sx={{ color: theme.palette.custom.gray[600] }}>
                        {downloadingId === report.id
                          ? <CircularProgress size={18} />
                          : <SvgIcon sx={{ width: '18px' }}><DownloadCloud01 /></SvgIcon>}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default RecentReportsPanel;
